import { useEffect, useState } from 'react';
import client from '../../helpers/Client';
import formatDate from '../../helpers/formatDate';
import {
  Text, 
  Box, 
  IconButton, 
  VStack, 
  HStack, 
  Icon, 
  Circle, 
  Flex} from '@chakra-ui/react';
import {TbRefresh} from "react-icons/tb";
import {
  FaPlus,
  FaUser,
  FaPlayCircle,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaEdit,
  FaBuilding,
  FaUserTimes
} from "react-icons/fa";

String.prototype.template = function (d) { // eslint-disable-line no-extend-native
  return this.replace(/{([^}]+)}/g, function (m, n) {
    var o = d, p = n.split('|')[0].split('.');
    for (var i = 0; i < p.length; i++) {
      o = typeof o[p[i]] === "function" ? o[p[i]]() : o[p[i]];
      if (typeof o === "undefined" || o === null) return n.indexOf('|') !== -1 ? n.split('|')[1] : m;
    }
    return o;
  });
};

const TAG_MAP = {
  // Tickets
  'started': 'başlandı olarak işaretlendi.',
  'cancelled': 'iptal edildi.',
  'completed': 'tamamlandı olarak işaretlendi',
  'assigned': 'şu bayiye atandı: {assignment.name}',
  'unassigned': 'bayi ataması iptal edildi', 
  // Service records
  'sent_to_base': 'merkeze gönderildi olarak işaretlendi.',
  'received_by_base': 'merkez tarafından teslim alındı olarak işaretlendi.',
  'eval_started': 'incelemeye alındı olarak işaretlendi.',
  'customer_accepted': 'ücret onaylandı olarak işaretlendi.',
  'customer_rejected': 'ücret reddedildi olarak işaretlendi.',
  'progress_started': 'işleme alındı olarak işaretlendi.',
  'sent_to_reseller': 'bayiye gönderildi olarak işaretlendi.',
  'received_by_reseller': 'bayi tarafından teslim alındı olarak işaretlendi.',
  'failed': 'başarısız olarak işaretlendi.',
  'deleted': 'silindi.'
};

const getLogIcon = (kind) => {
  switch (kind) {
    case 'created':
      return FaPlus;
    case 'assigned':
      return FaUser;
    case 'unassigned':
      return FaUserTimes;
    case 'started':
      return FaPlayCircle;
    case 'completed':
      return FaCheckCircle;
    case 'cancelled':
      return FaTimesCircle;
    case 'sent_to_base':
      return FaBuilding;
    case 'received_by_base':
      return FaCheckCircle;
    case 'eval_started':
      return FaClock;
    case 'customer_accepted':
      return FaCheckCircle;
    case 'customer_rejected':
      return FaTimesCircle;
    case 'progress_started':
      return FaPlayCircle;
    case 'sent_to_reseller':
      return FaBuilding;
    case 'received_by_reseller':
      return FaCheckCircle;
    case 'failed':
      return FaTimesCircle;
    case 'deleted':
      return FaTimesCircle;
    default:
      return FaEdit;
  }
};

const getLogColor = (kind) => {
  switch (kind) {
    case 'created':
      return { color: 'blue.500', bgColor: 'blue.50' };
    case 'assigned':
      return { color: 'purple.500', bgColor: 'purple.50' };
    case 'unassigned':
      return { color: 'gray.500', bgColor: 'gray.50' };
    case 'started':
      return { color: 'green.500', bgColor: 'green.50' };
    case 'completed':
      return { color: 'green.600', bgColor: 'green.50' };
    case 'cancelled':
      return { color: 'red.500', bgColor: 'red.50' };
    case 'sent_to_base':
      return { color: 'orange.500', bgColor: 'orange.50' };
    case 'received_by_base':
      return { color: 'green.600', bgColor: 'green.50' };
    case 'eval_started':
      return { color: 'blue.600', bgColor: 'blue.50' };
    case 'customer_accepted':
      return { color: 'green.600', bgColor: 'green.50' };
    case 'customer_rejected':
      return { color: 'red.500', bgColor: 'red.50' };
    case 'progress_started':
      return { color: 'green.500', bgColor: 'green.50' };
    case 'sent_to_reseller':
      return { color: 'orange.500', bgColor: 'orange.50' };
    case 'received_by_reseller':
      return { color: 'green.600', bgColor: 'green.50' };
    case 'failed':
      return { color: 'red.500', bgColor: 'red.50' };
    case 'deleted':
      return { color: 'red.500', bgColor: 'red.50' };
    default:
      return { color: 'gray.500', bgColor: 'gray.50' };
  }
};

function Log({ log, isLast, borderColor, textColor, mutedTextColor }) {
  let sentence;
  
  if (log.kind === 'assigned') {
    if (log.assignment && log.assignment.name) {
      sentence = TAG_MAP[log.kind].template(log);
    } else {
      sentence = 'atanmış bayi kaldırıldı';
    }
  } else {
    sentence = (TAG_MAP[log.kind] && TAG_MAP[log.kind].template(log)) || log.kind;
  }

  const logIcon = getLogIcon(log.kind);
  const logColors = getLogColor(log.kind);
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('tr-TR', {
        day: 'numeric',
        month: 'short'
      }),
      time: date.toLocaleTimeString('tr-TR', {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };

  const { date, time } = formatDate(log.created_at);
  
  return (
    <Box position="relative">
      <HStack spacing={4} align="start" py={3}>
        {/* Timeline Line and Icon */}
        <Box position="relative" zIndex={2}>
          <Circle
            size={8}
            bg={logColors.bgColor}
            border="2px solid"
            borderColor={logColors.color}
          >
            <Icon as={logIcon} color={logColors.color} boxSize={3} />
          </Circle>
          
          {/* Vertical Line */}
          {!isLast && (
            <Box
              position="absolute"
              left="50%"
              top="32px"
              width="2px"
              height="calc(100% + 12px)"
              bg={borderColor}
              transform="translateX(-50%)"
              zIndex={1}
            />
          )}
        </Box>

        {/* Event Content */}
        <Box flex={1} pb={!isLast ? 3 : 0}>
          <Flex justify="space-between" align="start" mb={1}>
            <VStack align="start" spacing={0}>
              <Text fontSize="sm" fontWeight="medium" color={textColor}>
                {(log.actor?.first_name || log.actor?.username || "Sistem")} tarafından {sentence}
              </Text>
            </VStack>
            
            <VStack align="end" spacing={0}>
              <Text fontSize="xs" fontWeight="semibold" color={logColors.color}>
                {date}
              </Text>
              <Text fontSize="xs" color={mutedTextColor}>
                {time}
              </Text>
            </VStack>
          </Flex>
        </Box>
      </HStack>
    </Box>
  );
}

export default function Logs({ subjectType, subjectID, ...props }) {
  const [logs, setLogs] = useState(null);
  
  const borderColor = "gray.200";
  const textColor = "gray.800";
  const mutedTextColor = "gray.600";

  useEffect(() => {
    if (!subjectType || !subjectID) {
      console.warn('Logs component: subjectType or subjectID is undefined');
      return;
    }
    client.get('/logs/' + subjectType + '/', {params: {subject: subjectID}}).then((response) => {
      setLogs(response.data);
    }).catch((error) => {
      console.error('Error fetching logs:', error);
    });
  }, [subjectType, subjectID]);

  const refreshLogs = () => {
    if (!subjectType || !subjectID) {
      console.warn('Logs component: subjectType or subjectID is undefined');
      return;
    }
    client.get('/logs/' + subjectType + '/', {params: {subject: subjectID}}).then((response) => {
      setLogs(response.data);
    }).catch((error) => {
      console.error('Error fetching logs:', error);
    });
  };

  return (
    <Box {...props}>
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontWeight={'bold'} color={mutedTextColor}>Hareketler</Text>
        <IconButton 
          aria-label='Hareketleri yenile' 
          icon={<TbRefresh />} 
          onClick={refreshLogs}
          size="sm"
          variant="ghost"
        />
      </Flex>
      
      <VStack spacing={0} align="stretch">
        {logs && logs.map((log, i) => (
          <Log 
            key={'log-' + i} 
            log={log} 
            isLast={i === logs.length - 1}
            borderColor={borderColor}
            textColor={textColor}
            mutedTextColor={mutedTextColor}
          />
        ))}
        
        {(!logs || logs.length === 0) && (
          <Text fontSize="sm" color={mutedTextColor} textAlign="center" py={4}>
            Henüz hareket bulunmuyor
          </Text>
        )}
      </VStack>
    </Box>
  );
}
